import React, { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useAtom } from 'jotai';

import FilterMenu from './Components/Filters/FilterMenu';
import AdvancedFilterMenuOverlay from './Components/Filters/AdvancedFilterMenuOverlay';
import Container from './Components/Container';
import { closeFilterMenu } from './utils/filters';
import { resetFastFilters, resetNavFilters, resetURLParams } from './utils/reset';

import { applyStateAtom, initialState } from './utils/globals';
import URLParamsControllers from './utils/controllers';

const App = ({
    filterLinks,
    restUrl,
    seriesId,
    difficultyValues,
    mainIngredientValues,
    cookingTimeValues,
    courseValues,
    dietValues,
    styleValues,
    fuelTypeValues,
    featuredValues,
    enableProteinFilter,
}) => {
    const [applyState, setApplyState] = useAtom(applyStateAtom);
    const [currentPage, setCurrentPage] = useState(1);
    const [hideLoadMore, setHideLoadMore] = useState(false);
    const [results, setResults] = useState([]);
    const [resultsCounter, setResultsCounter] = useState('0');
    const [activeFilters, setActiveFilters] = useState([]);
    const [activeFiltersCounter, setActiveFiltersCounter] = useState(0);
    const [disableApplyFilters, setApplyFilters] = useState(true);
    const filterRef = useRef(null);

    // URL Params Controllers that should only run once on page load, to set the new applyState.
    useEffect(() => {
        const newApplyState = URLParamsControllers(
            activeFilters,
            setActiveFilters,
            setActiveFiltersCounter,
            applyState,
            setApplyState,
            filterRef,
            difficultyValues,
            mainIngredientValues,
            cookingTimeValues,
            courseValues,
            dietValues,
            styleValues,
            fuelTypeValues,
            featuredValues
        );

        setApplyState(newApplyState);
    }, []);

    const api = axios.create({
        baseURL: restUrl,
    });

    function queryBuilder(page = 1, limit = 12) {
        const { effortLevel, mainIngredients, cookingTime, course, diet, style, fuelType, featured, orderby } =
            applyState;

        // Need Banner Color from College
        const requiredFields = [
            'banner_color',
            'id',
            'icon_name',
            'image',
            'link',
            'rating',
            'recipe.image_url',
            'recipe.name',
        ];
        let query = `?wprm_type=food&page=${page}&per_page=${limit}&_fields=${requiredFields.join(',')}`;

        if (seriesId) {
            query += `&wprm_series_and_groups=${seriesId}`;
        }
        if (effortLevel.length) {
            query += `&wprm_difficulty=${effortLevel.join(',')}`;
        }
        if (mainIngredients.length) {
            query += `&wprm_main_ingredient=${mainIngredients.join(',')}`;
        }
        if (cookingTime.length) {
            query += `&wprm_cooking_time=${cookingTime.join(',')}`;
        }
        if (course.length) {
            query += `&wprm_course=${course.join(',')}`;
        }
        if (diet.length) {
            query += `&wprm_diet=${diet.join(',')}`;
        }
        if (style.length) {
            query += `&wprm_style=${style.join(',')}`;
        }
        if (fuelType.length) {
            query += `&wprm_fuel_type=${fuelType.join(',')}`;
        }
        if (featured.length) {
            query += `&wprm_featured=${featured.join(',')}`;
        }
        if (orderby.length) {
            query += `${orderby}`;
        }

        return query;
    }

    useEffect(() => {
        setCurrentPage(1);
    }, [applyState]);

    // The queryBuilder callback depends on the applyState object each time it changes.
    const queryBuilderCb = useCallback(queryBuilder, [applyState]);

    // Run the API call each time the query builder changes.
    useEffect(() => {
        makeAPICall();
    }, [queryBuilderCb]);

    function makeAPICall() {
        api.get(queryBuilderCb())
            .then((res) => {
                setHideLoadMore(false);
                if (res.data.length < 12 || parseInt(res.headers['x-wp-totalpages'], 10) < 2) {
                    setHideLoadMore(true);
                }
                setResultsCounter(res.headers['x-wp-total']);
                setResults(res.data);
            })
            .catch((err) => {
                console.warn(err);
                setHideLoadMore(true);
            });
    }

    function applyFilters() {
        closeFilterMenu(setApplyFilters);
        resetFastFilters();
        makeAPICall();
    }

    function resetFilters() {
        // remove all values from all keys in applyState
        Object.keys(applyState).forEach((key) => {
            applyState[key] = [];
        });
        // applyState.order = initialState.order;
        applyState.orderby = initialState.orderby;
        setApplyState(applyState);

        const checkboxes = document.getElementsByClassName('js-filter-checkbox');
        for (let i = 0; i < checkboxes.length; i += 1) {
            if (checkboxes[i].checked) {
                checkboxes[i].click();
            }
        }

        closeFilterMenu(setApplyFilters);
        resetNavFilters(setActiveFilters, setActiveFiltersCounter, setApplyState);
        resetURLParams();
        resetFastFilters();
        setActiveFiltersCounter(0);
        makeAPICall();
    }

    function loadMore() {
        const newValue = currentPage + 1;

        api.get(queryBuilderCb(newValue))
            .then((res) => {
                setHideLoadMore(false);
                if (res.data.length < 12 || parseInt(res.headers['x-wp-totalpages'], 10) < 2) {
                    setHideLoadMore(true);
                }
                setResultsCounter(res.headers['x-wp-total']);
                setResults((prev) => [...prev, ...res.data]);
            })
            .catch(() => {
                setHideLoadMore(true);
            });
        setCurrentPage(newValue);
    }

    return (
        <Fragment>
            <FilterMenu
                activeFilters={activeFilters}
                setActiveFilters={setActiveFilters}
                setActiveFiltersCounter={setActiveFiltersCounter}
                setApplyFilters={setApplyFilters}
                disableApplyFilters={disableApplyFilters}
                resetFilters={resetFilters}
                enableProteinFilter={enableProteinFilter}
                mainIngredientValues={mainIngredientValues}
                courseValues={courseValues}
                difficultyValues={difficultyValues}
                cookingTimeValues={cookingTimeValues}
                dietValues={dietValues}
                fuelTypeValues={fuelTypeValues}
                featuredValues={featuredValues}
                styleValues={styleValues}
                applyFilters={applyFilters}
            />

            <AdvancedFilterMenuOverlay />

            <Container
                activeFiltersCounter={activeFiltersCounter}
                filterLinks={filterLinks}
                filterRef={filterRef}
                activeFilters={activeFilters}
                setActiveFilters={setActiveFilters}
                setActiveFiltersCounter={setActiveFiltersCounter}
                makeAPICall={makeAPICall}
                resultsCounter={resultsCounter}
                resetFilters={resetFilters}
                results={results}
                hideLoadMore={hideLoadMore}
                loadMore={loadMore}
            />
        </Fragment>
    );
};
export default App;
